import React, { useEffect, useRef } from "react";
import { Send, Loader2, MessageSquare, ArrowLeft } from "lucide-react";
import CompactUsageIndicator from "../tailspin/CompactUsageIndicator";

export default function CollateralChatbot({
  messages,
  inputValue,
  isSending,
  onInputChange,
  onSendMessage,
  onKeyPress,
  allVersions = [],
  currentVersionIndex = 0,
  onVersionSwitch,
  usageStats = null,
  currentVersions = 0,
  hasRunningVersion = false,
}) {
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Chat Header */}
      {/* <div className="px-6 py-4 border-b border-gray-200 bg-[#fbf4e9]">
        <div className="flex items-center gap-3">
          <img 
            src="https://i.ibb.co/MvVKzjj/file-00000000a5dc7209bf802deb262dc9a8.png" 
            alt="Parrot Intelligence Logo" 
            className="h-10 w-12.5 rounded-sm    border border-white"
            style={{ minWidth: '40px', minHeight: '40px' }}
          />
          <div>
            {/* <h2 className="text-lg font-semibold text-gray-700">Collateral Editor</h2> 
          </div> 
        </div>
      </div> */}

      {/* Messages Container */}
      <div 
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
        style={{ maxHeight: 'calc(100vh - 200px)' }}
      >
        {messages.length === 0 && allVersions.length === 1 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <MessageSquare className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm mb-2">No messages yet</p>
            <p className="text-gray-400 text-xs">
              Ask me to edit the collateral, update content, or change styling
            </p>
          </div>
        ) : (
          <>
            {messages.length > 0 && messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    message.type === 'user'
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                      : message.isError
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                  
                  {/* Version History for bot messages with versions */}
                  {message.type === 'bot' && message.versions && message.versions.length > 1 && (
                    <div className="mt-3 pt-3 border-t border-gray-300">
                      <p className="text-xs font-semibold text-gray-600 mb-2">Previous Versions:</p>
                      <div className="flex flex-col gap-2">
                        {message.versions.slice(1).map((version, idx) => (
                          <button
                            key={idx + 1}
                            onClick={() => onVersionSwitch && onVersionSwitch(idx + 1)}
                            className="flex items-center gap-2 text-xs text-gray-700 hover:text-blue-600 hover:bg-gray-200 px-2 py-1 rounded transition-colors"
                          >
                            <ArrowLeft className="h-3 w-3" />
                            <span>Version {idx + 2}</span>
                            {version.title && (
                              <span className="text-gray-500 text-xs">({version.title})</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <p
                    className={`text-xs mt-1 ${
                      message.type === 'user'
                        ? 'text-blue-100'
                        : message.isError
                        ? 'text-red-500'
                        : 'text-gray-500'
                    }`}
                  >
                    {message.timestamp}
                  </p>
                </div>
              </div>
            ))}
            
            {/* Version History Sidebar */}
            {allVersions.length > 1 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-600 mb-2">All Versions:</p>
                <div className="flex flex-col gap-1">
                  {allVersions.map((version, idx) => {
                    const status = version.status;
                    const isRunning = status === 'running';
                    const isFailed = status === 'failed';
                    const isDone = !status || status === 'done';
                    
                    return (
                    <button
                      key={idx}
                      onClick={() => onVersionSwitch && onVersionSwitch(idx)}
                        disabled={isRunning || isFailed}
                      className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded transition-colors ${
                        idx === currentVersionIndex
                          ? 'bg-blue-100 text-blue-700 font-semibold'
                            : isRunning
                            ? 'text-orange-600 bg-orange-50 cursor-wait'
                            : isFailed
                            ? 'text-red-600 bg-red-50 cursor-not-allowed'
                          : 'text-gray-600 hover:text-blue-600 hover:bg-gray-100'
                      }`}
                    >
                        {idx < currentVersionIndex && isDone && <ArrowLeft className="h-3 w-3" />}
                        {isRunning && <Loader2 className="h-3 w-3 animate-spin" />}
                      <span>Version {idx + 1}</span>
                        {idx === 0 && isDone && <span className="text-gray-500 text-xs">(Latest)</span>}
                        {isRunning && <span className="text-orange-600 text-xs">(Generating...)</span>}
                        {isFailed && <span className="text-red-600 text-xs">(Failed)</span>}
                        {version.title && idx !== currentVersionIndex && isDone && (
                        <span className="text-gray-500 text-xs truncate max-w-[150px]">({version.title})</span>
                      )}
                    </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        {/* Compact Usage Indicator */}
        {usageStats && (
          <div className="mb-3">
            <CompactUsageIndicator 
              stats={usageStats}
              currentVersions={currentVersions}
              type="edit"
            />
          </div>
        )}

        {/* Generation in Progress Warning */}
        {hasRunningVersion && (
          <div className="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-2">
            <Loader2 className="h-4 w-4 text-orange-600 animate-spin mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-semibold text-orange-800">Generation in Progress</p>
              <p className="text-xs text-orange-700 mt-1">
                Please wait for the current generation to complete before sending another request.
              </p>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <textarea
              value={inputValue}
              onChange={onInputChange}
              onKeyPress={onKeyPress}
              maxLength={1000}
              placeholder={hasRunningVersion ? "Waiting for generation to complete..." : "Describe your changes..."}
              disabled={isSending || hasRunningVersion}
              className="flex-1 resize-none rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
              rows="3"
            />
            <button
              onClick={onSendMessage}
              disabled={isSending || hasRunningVersion || !inputValue.trim()}
              className="self-end px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2 font-medium shadow-md hover:shadow-lg"
              title={hasRunningVersion ? "Please wait for current generation to complete" : ""}
            >
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-xs">Sending</span>
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  <span className="text-xs">Send</span>
                </>
              )}
            </button>
          </div>
          <div className="text-xs text-gray-500 text-right px-1">
            {inputValue.length} / 1,000 characters
          </div>
        </div>
        {!hasRunningVersion && (
        <p className="text-xs text-gray-500 mt-2 px-1">
          Press <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">Enter</kbd> to send, <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">Shift+Enter</kbd> for new line
        </p>
        )}
      </div>
    </div>
  );
}

